import gsap from 'gsap'
import ScrollToPlugin from 'gsap/ScrollToPlugin'
import ScrollTrigger from 'gsap/ScrollTrigger'
import CustomEase from 'gsap/CustomEase'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

gsap.registerPlugin(ScrollToPlugin)
gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(CustomEase)

const SELECTORS = {
  bgVideo: '.js-pride-campaign__bg-video',
  arrow: '.js-pride-campaign__arrow-down',
  mediaButton: '.js-pride-campaign__media-button',
  introVideo: '.js-pride-campaign__intro-video',
  introVideoOverlay: '.js-video-player-overlay',
  introVideoIFrame: '.js-video-player-iframe',
  wpAdminBar: '#wpadminbar',
  microsoftHeader: '#headerArea',
  videoButtonPlayIcon: '.js-card-video-button-play-icon',
  videoButtonPauseIcon: '.js-card-video-button-pause-icon'
}

class PrideHeader {
  constructor (element) {
    this.element = element
    this.bgVideo = this.element.querySelector(SELECTORS.bgVideo)
    this.arrow = this.element.querySelector(SELECTORS.arrow)
    this.videoButton = this.element.querySelector(SELECTORS.mediaButton)
    this.introVideo = this.element.querySelector(SELECTORS.introVideo)
    this.introVideoOverlay = this.element.querySelector(SELECTORS.introVideoOverlay)
    this.videoEnded = false
    this.wpAdminBar = document.body.querySelector(SELECTORS.wpAdminBar)
    this.wpAdminBarHeight = this.wpAdminBar ? this.wpAdminBar.clientHeight : 0
    this.microsoftHeader = document.body.querySelector(SELECTORS.microsoftHeader)
    this.microsoftHeaderHeight = this.microsoftHeader ? this.microsoftHeader.clientHeight : 0
    this.tl = null
    this.videoButtonPlayIcon = this.videoButton.querySelector(SELECTORS.videoButtonPlayIcon)
    this.videoButtonPauseIcon = this.videoButton.querySelector(SELECTORS.videoButtonPauseIcon)
    this.init()
    this.isVideoPlaying = null
  }

  init() {
    this.registerEvents()
    this.setIntroVideo()
    gsap.set(this.element, { height: '100vh' })

    if (
      this.bgVideo.paused === false &&
      this.bgVideo.ended === false
    ) {
      this.videoButton.style.display = ''
    }
  }

  registerEvents() {
    this.bgVideo.addEventListener('timeupdate', (event) => this.onVideoEnd(event))

    this.bgVideo.oncanplay = () => {
      this.showPauseButton()
    }

    this.bgVideo.onplaying = () => {
      this.handleVideoPlaying()
    }

    this.bgVideo.onended = () => {
      this.handleVideoPaused()

      setTimeout(() => {
        this.videoButton.style.display = 'none'
      }, 600)
    }

    this.bgVideo.onpause = () => {
      this.handleVideoPaused()
    }

    this.videoButton.onclick = (event) => this.handleVideoButtonClick(event)
  }

  onVideoEnd(event) {
    if (this.videoEnded) return
    if (document.documentElement.classList.contains('reduced-motion')) return

    const scrollAmount = 80 + this.wpAdminBarHeight + this.microsoftHeaderHeight
    const videoHidden = this.introVideoOverlay.getBoundingClientRect().top >= scrollAmount
    this.videoEnded = event.target.currentTime >= 6

    if (this.videoEnded && videoHidden) {
      gsap.to(window, { scrollTo: scrollAmount, duration: 0.62, ease: 'bezierPride' })
    }
  }

  showPauseButton() {
    this.videoButton.style.display = ''
  }

  setIntroVideo() {
    CustomEase.create('bezierPride', '0.25,0.1,0.25,1.0')
    this.introVideoOverlay.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)

    const scrollStart = this.wpAdminBarHeight + this.microsoftHeaderHeight + 160
    const scrollEnd = this.wpAdminBarHeight + this.microsoftHeaderHeight + 80

    this.tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.introVideo,
        start: `top +=${scrollStart}px`,
        end: `top +=${scrollEnd}px`,
        scrub: 1,
        onEnter: () => {
          const introVideoIFrame = this.element.querySelector(SELECTORS.introVideoIFrame)
          const videoIsPlaying = introVideoIFrame.classList.contains(GLOBAL_CONSTANTS.CLASSES.PLAYING)

          gsap.set(this.element, { height: 'initial' })

          if (!videoIsPlaying) {
            this.introVideoOverlay.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
          }
        },
        onLeaveBack: () => {
          gsap.set(this.element, { height: '100vh' })
        }
      }
    })
    this.tl.to([this.bgVideo, this.arrow, this.videoButton], {
      opacity: 0,
      duration: 0.3,
      ease: 'bezierPride'
    })
    this.tl.to(this.introVideo, { opacity: 1, duration: 0.8, ease: 'bezierPride' })
  }

  handleVideoButtonClick(event) {
    event.preventDefault()
    if (this.isVideoPlaying) {
      this.bgVideo.pause()
    } else {
      this.bgVideo.play()
    }
  }

  handleVideoPaused() {
    this.isVideoPlaying = false
    if (this.videoButton) {
      this.videoButtonPlayIcon.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButtonPauseIcon.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButton.ariaLabel = 'play video'
    }
  }

  handleVideoPlaying() {
    this.isVideoPlaying = true
    if (this.videoButton) {
      this.videoButtonPlayIcon.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButtonPauseIcon.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.videoButton.ariaLabel = 'pause video'
    }
  }
}

export default {
  selector: '.js-pride-campaign__header',
  Source: PrideHeader
}
